import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AccessDenied from "./ui/AccessDenied";

/*Import UTILS */
import { renderHighlightedPath } from "./utils/renderHighlightedPath";

/*Import podkomponente */
import ProgramsList from "./programs/ProgramsList";

/*Import API */
import {
  iskanjeProgramStrukturaNivo1,
  prviNivoProgramiAPI,
} from "../listOfApi";
import { fetchGET } from "../apiHelper";

/* React-Bootstrap component */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

/*import React-icons */
import { AiOutlineExclamationCircle } from "react-icons/ai";

import RoleGuard from "../auth/RoleGuard";
import { MODULES, ROLE_GROUPS } from "../auth/roles";

const Programs = () => {
  const MIN_SEARCH_LENGTH = 3;
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [programsLoaded, setProgramsLoaded] = useState(false);
  const [searchProgram, setSearchProgram] = useState("");
  const [showActive, setShowActive] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchProgramDebounced, setSearchProgramDebounced] = useState("");
  const [searchStructureResults, setSearchStructureResults] = useState([]);

  useEffect(() => {
    fetchGET(prviNivoProgramiAPI).then((programi) => {
      if (programi !== 0) {
        setPrograms(programi);
        setFilteredPrograms(programi);
        setProgramsLoaded(true);
      }
    });
  }, []);

  /*Filtriranje seznama ki se prikaže glede na status checkboxov "Aktivni" in "Neaktivni" */
  useEffect(() => {
    let filtered = programs;

    // Reversed logic: unchecked = show all
    if (!showActive && !showInactive) {
      // Both unchecked - show all programs
      filtered = programs;
    } else if (showActive && !showInactive) {
      // Only active checked - show only active
      filtered = programs.filter((program) => program.aktiven);
    } else if (!showActive && showInactive) {
      // Only inactive checked - show only inactive
      filtered = programs.filter((program) => !program.aktiven);
    } else if (showActive && showInactive) {
      // Both checked - show all programs
      filtered = programs;
    }

    setFilteredPrograms(filtered);
  }, [showActive, showInactive, programs]);

  /*Debouncing iskanja v strukturi programa, da se zahteva po iskanju sproži šele po 300ms od zadnje spremembe v iskalnem polju */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchProgramDebounced(searchProgram.trim());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchProgram]);

  /*Iskanje v strukturi programa na podlagi iskalnega izraza, ki se sproži ob spremembi debounced iskalnega izraza */
  useEffect(() => {
    let isActive = true;

    if (searchProgramDebounced.length < MIN_SEARCH_LENGTH) {
      setSearchStructureResults([]);
      return () => {
        isActive = false;
      };
    }

    const endpoint = `${iskanjeProgramStrukturaNivo1}?query=${encodeURIComponent(
      searchProgramDebounced,
    )}`;

    fetchGET(endpoint).then((response) => {
      if (!isActive) {
        return;
      }

      if (response === 0 || !Array.isArray(response)) {
        setSearchStructureResults([]);
        return;
      }

      setSearchStructureResults(response);
    });

    return () => {
      isActive = false;
    };
  }, [MIN_SEARCH_LENGTH, searchProgramDebounced]);

  // Klik na enega izmed rezultatov iskanja v strukturi programa nas preusmeri na stran s podrobnostmi o tem programu (struktura)
  const handleSearchResultClick = (rootID) => {
    setSearchStructureResults([]);
    navigate({
      pathname: `/Programi/Struktura/${rootID}`,
      search: `?search=${encodeURIComponent(searchProgram)}`,
    });
  };

  /*Pokaže dropdown z rezultati iskanja v strukturi programa, če je iskalni izraz dovolj dolg in so na voljo rezultati */
  const showSearchStructureDropdown =
    searchProgram.trim().length >= MIN_SEARCH_LENGTH &&
    searchStructureResults.length > 0;

  const isSearching = searchProgram.trim().length >= MIN_SEARCH_LENGTH;

  return (
    <Container fluid>
      <RoleGuard
        moduleKey={MODULES.PROGRAMI}
        roles={ROLE_GROUPS.GLOBALNI_AND_FAKULTETNI}
        fallback={<AccessDenied />}
      >
        <Row>
          <Col className="main-header font-weight-bold">Programi</Col>
        </Row>
        <Row className="mb-2">
          <Col className="header-divider">
            <hr />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col lg={6} md={8}>
            <Form.Control
              name="iskalnik"
              type="text"
              value={searchProgram}
              onChange={(e) => setSearchProgram(e.target.value)}
              placeholder="Poišči po nazivu programa, podprograma ..."
            />
          </Col>
          <Col>
            <div className="float-end">
              <RoleGuard
                moduleKey={MODULES.PROGRAMI}
                roles={ROLE_GROUPS.EDITORS}
              >
                <Link
                  className="programs-button-add-text"
                  to={`/Programi/DodajProgram/`}
                >
                  <Button variant="outline-primary">Dodaj program</Button>
                </Link>
              </RoleGuard>
            </div>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col className="header-divider">
            <hr style={{ borderTop: "2px solid #616263", margin: "1rem 0" }} />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <div className="mb-2">
              <strong>Status</strong>
            </div>
            <div className="d-flex gap-3">
              <Form.Check
                name="aktivni"
                type="checkbox"
                checked={showActive}
                onChange={() => setShowActive(!showActive)}
                label="Aktivni"
              />
              <Form.Check
                name="neaktivni"
                type="checkbox"
                checked={showInactive}
                onChange={() => setShowInactive(!showInactive)}
                label="Neaktivni"
              />
            </div>
          </Col>
        </Row>

        {showSearchStructureDropdown && (
          <Row className="mb-2">
            <Col>
              <div className="programs-page-search-results">
                {searchStructureResults.map((result) => (
                  <button
                    key={`${result.objectID}-${result.path}`}
                    type="button"
                    className="programs-page-search-results__path"
                    onClick={() => handleSearchResultClick(result.rootID)}
                  >
                    {renderHighlightedPath(result.path, searchProgram, 3)}
                  </button>
                ))}
              </div>
            </Col>
          </Row>
        )}

        <div>
          {/* Komponenta ProgramsList za prikaz seznama programov */}
          {programsLoaded && !isSearching && (
            <ProgramsList programs={filteredPrograms} />
          )}
        </div>

        {!programsLoaded && (
          <Row className="mt-3">
            <Col>
              <h5>
                <span className="me-3">
                  <AiOutlineExclamationCircle />
                </span>
                Nalaganje programov...
              </h5>
            </Col>
          </Row>
        )}
      </RoleGuard>
    </Container>
  );
};

export default Programs;
